import { useMemo, useState } from 'react';

import { LngLat } from 'mapbox-gl';
import { Marker } from 'react-map-gl';

import { BambiMapSearch } from 'src/common/BambiMapView/BambiMapSearch';
import {
  BambiMapView,
  MapViewProps,
} from 'src/common/BambiMapView/BambiMapView';
import { GEO_HYBRID_MAP_RESOURCE } from 'src/common/external/aws';
import colors from 'src/common/theme/colors';

import { roundCoordinates } from './helpers';
import { Coordinates } from './types';

export type OnSelectLocation = (coordinates: Coordinates) => void;

interface GeolocationMapViewProps {
  onSelect: OnSelectLocation;
  coordinates?: LngLat;
  canEditCoordinates: boolean;
}

type InitialViewState = MapViewProps['initialViewState'];

export function GeolocationMapView({
  onSelect,
  coordinates,
  canEditCoordinates,
}: GeolocationMapViewProps) {
  const [marker, setMarker] = useState<LngLat | undefined>(coordinates);
  const onClick = (e: mapboxgl.MapMouseEvent) => {
    const roundedCoordinates = roundCoordinates({
      latitude: e.lngLat.lat,
      longitude: e.lngLat.lng,
    });
    setMarker(
      new LngLat(roundedCoordinates.longitude, roundedCoordinates.latitude)
    );
    onSelect(roundedCoordinates);
  };

  const initialViewState: InitialViewState | undefined = useMemo(() => {
    // Customize the initial view state if loading an existing geolocation
    if (coordinates) {
      return {
        zoom: 12,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
      };
    }
    return undefined;
  }, [coordinates]);

  return (
    <div className="h-full">
      <BambiMapView
        mapViewProps={{
          onClick: canEditCoordinates ? onClick : undefined,
          cursor: canEditCoordinates ? 'crosshair' : 'grab',
          mapStyle: GEO_HYBRID_MAP_RESOURCE,
          initialViewState,
        }}
      >
        <BambiMapSearch flyToZoom={17} markerColor={colors.indigo} />
        {marker && (
          <Marker
            longitude={marker.lng}
            latitude={marker.lat}
            anchor="bottom"
            offset={[0, 5]} // Needed to get the tip of the marker to align with the crosshair
            color={colors.mint}
          />
        )}
      </BambiMapView>
    </div>
  );
}
